import clsx from 'clsx';
import React, { forwardRef } from 'react';
import { Label } from './Label';
import { OptionalDescriptionTooltip } from './OptionalDescriptionTooltip';
import { InputError } from './InputError';
import { Skeleton } from './Skeleton';

type InputProps = {
	error?: string;
	description?: string;
	label: string;
	labelHint?: string;
	id: string;
	obligatory?: boolean;
	className?: string;
	inputClassName?: string;
	disabled?: boolean;
	hideFromUi?: boolean;
} & React.InputHTMLAttributes<HTMLInputElement>;

type Variant = 'normal' | 'error' | 'disabled';

const Input = forwardRef<HTMLInputElement, InputProps>(
	(
		{
			error,
			className,
			inputClassName,
			obligatory,
			disabled,
			label,
			labelHint,
			description,
			hideFromUi,
			id,
			...forwardProps
		},
		ref
	) => {
		let variant: Variant = 'normal';
		if (error) {
			variant = 'error';
		} else if (disabled) {
			variant = 'disabled';
		}

		return (
			<div
				className={clsx(
					!hideFromUi && 'flex flex-col gap-1',
					hideFromUi && 'hidden',
					className
				)}
			>
				<div className="flex items-center gap-1">
					<Label htmlFor={id} label={label} required={obligatory} />
					{labelHint && <p className="text-sm text-gray-700">{labelHint}</p>}
					<OptionalDescriptionTooltip description={description} />
				</div>

				<input
					{...forwardProps}
					id={id}
					ref={ref}
					className={clsx(
						'rounded-md border w-full p-2 px-3 text-sm h-[40px]',
						variant === 'normal' && 'border-gray-400',
						variant === 'error' && 'border-error-500',
						variant === 'disabled' && 'border-gray-300 bg-gray-300 cursor-not-allowed',
						inputClassName
					)}
					disabled={disabled}
				/>

				{error && <InputError error={error} />}
			</div>
		);
	}
);

Input.displayName = 'Input';

export { Input };

export const InputSkeleton = () => (
	<div className="flex flex-col gap-1">
		<Skeleton className="w-40 h-4 rounded-sm" />
		<Skeleton className="w-full h-14 rounded-md" />
	</div>
);
